import React, { useCallback, useState, useEffect } from 'react';
import { Box, Container } from '@mui/material';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import SearchBar from './SearchBar';
import MapView from './MapView';
import ListView from './ListView';
import FilterDropdown from './FilterDropdown';
import { filterMapOffers, filterOffers } from 'utils/offerFilters';
import useOfferDisplayMode from 'components/offers/useOfferDisplayMode';

const InteractiveSearch = ({
  offers = [],
  mapOffers,
  onFilterChange,
  onMapViewportChange,
  userLocation,
  selectedFilter: propSelectedFilter,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMapVisible, setIsMapVisible] = useState(() => searchParams.get('map') === '1');
  const [isListVisible, setIsListVisible] = useState(() => searchParams.get('list') === '1');
  const [displayMode] = useOfferDisplayMode(searchParams.get('display'));
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  // Use prop filter if provided (from parent like LandingPage), otherwise manage internally
  const [internalFilter, setInternalFilter] = useState('none');
  const selectedFilter = propSelectedFilter !== undefined ? propSelectedFilter : internalFilter;
  // Use parent's onFilterChange if provided (from LandingPage), otherwise use internal state setter
  const handleFilterChange = onFilterChange || setInternalFilter;
  const [highlightedOfferId, setHighlightedOfferId] = useState(null);
  const [filteredOffers, setFilteredOffers] = useState(offers);
  const [mapBounds, setMapBounds] = useState(null);
  const radiusParam = searchParams.get('radius');

  useEffect(() => {
    // User radius from URL; when unset, backend enforces each offer's service_radius_miles
    let radius = null;
    if (radiusParam != null) {
      const parsed = parseFloat(radiusParam);
      radius = Number.isFinite(parsed) ? parsed : null;
    }
    
    const sourceOffers = isMapVisible && mapOffers !== undefined ? mapOffers : offers;
    const filtered = isMapVisible
      ? filterMapOffers(sourceOffers, mapBounds)
      : filterOffers(sourceOffers, selectedFilter, false, radius);

    const withDisplayIndex = filtered.map((offer, idx) => ({
      ...offer,
      displayIndex: idx + 1,
    }));

    setFilteredOffers(withDisplayIndex);
  }, [selectedFilter, offers, mapOffers, isMapVisible, mapBounds, radiusParam]);

const handleMapToggle = () => {
  if (isMapVisible) {
    // When map is already visible and user clicks again -> close both map and list
    setIsMapVisible(false);
    setIsListVisible(false);
    updateUrlParams(false, false);
  } else {
    // When map is currently hidden -> open map and always show list
    setIsMapVisible(true);
    setIsListVisible(true); // Always show list when map opens
    updateUrlParams(true, true);
  }
};


  // Sync map/list visibility from URL params controlled by the header.
  useEffect(() => {
    const mapParam = searchParams.get('map');
    const listParam = searchParams.get('list');

    if (mapParam === '1' || mapParam === '0') {
      setIsMapVisible(mapParam === '1');
    }

    if (listParam === '1' || listParam === '0') {
      const shouldShowList = listParam === '1';
      setIsListVisible(shouldShowList && mapParam === '1');
    }
  }, [searchParams]);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filterValue) => {
    // Update filter using the handler (either from parent or internal)
    handleFilterChange(filterValue);
  };

  const handleMarkerClick = (offerId) => {
    setHighlightedOfferId(offerId);
    // Auto-show list if not visible
    if (!isListVisible) {
      setIsListVisible(true);
    }
  };

  const handleOfferHover = (offerId) => {
    setHighlightedOfferId(offerId);
  };

  const handleMapViewportChange = useCallback((viewport) => {
    setMapBounds(viewport.bounds);
    onMapViewportChange?.(viewport);
  }, [onMapViewportChange]);

  // Update URL parameters when map/list state changes
  const updateUrlParams = (mapVisible, listVisible) => {
    const params = new URLSearchParams(location.search);
    
    if (mapVisible) {
      params.set('map', '1');
    } else {
      params.delete('map');
    }
    
    if (listVisible) {
      params.set('list', '1');
    } else {
      params.delete('list');
    }
    
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  // Determine layout based on visibility
  const showSplitView = isMapVisible && isListVisible;
  const effectiveDisplayMode = isMapVisible ? 'list' : displayMode;
  return (
    <Box sx={{ width: '100%' }}>
      {/* Search Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <SearchBar
          onMapToggle={handleMapToggle}
          onFilterClick={handleFilterClick}
          isMapActive={isMapVisible}
          isListActive={isListVisible}
          isDisplayActive={effectiveDisplayMode === 'list'}
          filterAnchorEl={filterAnchorEl}
        />
      </Box>

      {/* Filter Dropdown */}
      <FilterDropdown
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        selectedFilter={selectedFilter}
        onFilterSelect={handleFilterSelect}
      />

      {/* Content Area */}
      {(isMapVisible || isListVisible) && (filteredOffers.length > 0 || isMapVisible) && (
        <Container 
          maxWidth="xl" 
          sx={{ 
            px: { xs: 2, md: 3 },
            mx: { xs: 0, md: 'auto' },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 3, md: 2 }, // Increased mobile gap from 0 to 3
              flexDirection: { xs: 'column', md: showSplitView ? 'row' : 'column' },
              width: '100%',
            }}
          >
            {/* Map View */}
            {isMapVisible && (
              <Box
                sx={{
                  flex: showSplitView ? 1 : 'auto',
                  width: { xs: '100%', md: showSplitView ? '50%' : '100%' },
                  order: { xs: 1, md: showSplitView ? 'unset' : 1 },
                }}
              >
                <MapView
                  offers={filteredOffers}
                  onMarkerClick={handleMarkerClick}
                  highlightedOfferId={highlightedOfferId}
                  userLocation={userLocation}
                  isVisible={isMapVisible}
                  viewportBounds={mapBounds}
                  onViewportChange={handleMapViewportChange}
                />
              </Box>
            )}

            {/* List View */}
            {isListVisible && (
              <Box
                sx={{
                  flex: showSplitView ? 1 : 'auto',
                  width: { xs: '100%', md: showSplitView ? '50%' : '100%' },
                  order: { xs: 2, md: showSplitView ? 'unset' : 2 },
                  boxSizing: 'border-box',
                  margin: 0,
                  padding: 0,
                }}
              >
                <ListView
                  offers={filteredOffers}
                  highlightedOfferId={highlightedOfferId}
                  onOfferHover={handleOfferHover}
                  displayMode={effectiveDisplayMode}
                />
              </Box>
            )}
          </Box>
        </Container>
      )}

      {/* No results message */}
      {(isMapVisible || isListVisible) && filteredOffers.length === 0 && (
        <Container maxWidth="xl">
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            No offers found for the selected filter.
          </Box>
        </Container>
      )}

    </Box>
  );
};

export default InteractiveSearch;
